// Generated by GitHub Copilot
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FormSettingsEditor } from '#components/domain/operations/settings/form-settings-editor.tsx'
import { CustomFieldsEditor, type CustomFieldsAdapter } from '#components/domain/operations/settings/shared/custom-fields-editor.tsx'
import { AssetQrLayoutSettings } from '#components/domain/operations/assets/settings/asset-qr-layout-settings.tsx'
import { AssetStatusesSettings } from '#components/domain/operations/assets/settings/asset-statuses-settings.tsx'
import { AssetDowntimeReasonsSettings } from '#components/domain/operations/assets/settings/asset-downtime-reasons-settings.tsx'
import { AssetTagsSettings } from '#components/domain/operations/assets/settings/asset-tags-settings.tsx'
import { Tabs } from '#components/primitives/components/tabs.tsx'
import AssetsApi from '#core/api/operations/assets/Assets.ts'
import { AssetElevated, PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { useCanEditSetting } from '#core/entities/auth/roles/entity-settings-gates.ts'
import { useUserState } from '#core/state/UserState.ts'
import { useQuickSettingsReturn } from '#hooks/use-quick-settings-return.ts'

export const Route = createFileRoute('/_appSettings/$companyId/settings/assets')({
  component: RouteComponent,
})

const assetsApi = new AssetsApi()

const customFieldsAdapter: CustomFieldsAdapter = {
  load: () => assetsApi.getAssetCustomPropertiesDefinition(),
  save: (definition) => assetsApi.upsertAssetCustomPropertiesDefinition(definition),
}

/**
 * Asset field keys in the order they appear in the New Asset form. Keys must
 * match `AssetCreateRequestSchema` property names or the backend's allow-listed
 * virtual UI fields (`profile_picture`, `files`, `procedures`, `subscribers`) —
 * the base-form endpoint prunes any override whose key it does not recognise.
 */
const ASSET_FIELD_ORDER = [
  'name',
  'profile_picture',
  'files',
  'description',
  'asset_status_id',
  'parent_id',
  'custom_id',
  'serial_number',
  'qr_code_data',
  'bar_code_data',
  'model',
  'date_of_build',
  'vendors',
  'criticality',
  'tag_ids',
  'notes',
  'part_ids',
  'procedures',
  'subscribers',
] as const

const ASSET_ALWAYS_VISIBLE = ['name']
const ASSET_ALWAYS_REQUIRED = ['name']

type AssetsSettingsTab = 'form' | 'statuses' | 'downtime-reasons' | 'tags' | 'custom-fields' | 'qr-code' | 'direct-link-qr-code'

/** Settings page for Assets: form field settings and custom field definitions. */
function RouteComponent() {
  const { t } = useTranslation('settings')
  const { t: tGeneral } = useTranslation('general')
  const [activeTab, setActiveTab] = useState<AssetsSettingsTab>('form')
  const userState = useUserState()
  // Quick settings → Advanced → X round-trip, via the shared history-state hook.
  const { showClose, handleClose } = useQuickSettingsReturn()

  const canEditForm = userState.hasPermission(PermissionService.ASSETS, PermissionAction.FORM_SETTINGS)
  const canManageStatuses = userState.hasPermission(PermissionService.ASSETS, AssetElevated.MANAGE_STATUSES)
  const canManageDowntimeReasons = userState.hasPermission(PermissionService.ASSETS, AssetElevated.MANAGE_DOWNTIME_REASONS)
  const canManageTags = userState.hasPermission(PermissionService.ASSETS, AssetElevated.MANAGE_TAGS)
  const canManage = userState.hasPermission(PermissionService.ASSETS, PermissionAction.CUSTOM_PROPERTIES)
  const canManageQr = useCanEditSetting('asset', 'qr_code_template')
  const canManageDirectLinkQr = useCanEditSetting('asset', 'direct_link_qr_code_template')

  const tabs = [
    canEditForm && { key: 'form' as const, label: t('assetsSettings.tabForm') },
    canManageStatuses && { key: 'statuses' as const, label: t('assetsSettings.tabStatuses') },
    canManageDowntimeReasons && { key: 'downtime-reasons' as const, label: t('assetsSettings.tabDowntimeReasons') },
    canManageTags && { key: 'tags' as const, label: t('shared.fields.tags') },
    canManage && { key: 'custom-fields' as const, label: t('shared.fields.customFields') },
    canManageQr && { key: 'qr-code' as const, label: t('assetsSettings.tabQrCode') },
    canManageDirectLinkQr && { key: 'direct-link-qr-code' as const, label: t('assetsSettings.tabDirectLinkQrCode') },
  ].filter(Boolean) as { key: AssetsSettingsTab; label: string }[]

  // Fall back to the first permitted tab when the active one is hidden (e.g. a deep link).
  const effectiveTab = tabs.some((tb) => tb.key === activeTab) ? activeTab : tabs[0]?.key

  const fieldLabels: Record<string, string> = {
    name: t('shared.fields.name'),
    profile_picture: t('shared.fields.profilePicture'),
    files: t('shared.fields.attachments'),
    description: t('shared.fields.description'),
    asset_status_id: t('shared.fields.status'),
    parent_id: t('shared.fields.parentAsset'),
    custom_id: t('shared.fields.id'),
    serial_number: t('shared.fields.serialNumber'),
    qr_code_data: t('shared.fields.customQrCode'),
    bar_code_data: t('shared.fields.customBarcode'),
    model: t('shared.fields.model'),
    date_of_build: t('shared.fields.productionYear'),
    vendors: t('shared.fields.vendors'),
    criticality: t('shared.fields.criticality'),
    tag_ids: t('shared.fields.tags'),
    notes: t('shared.fields.notes'),
    part_ids: t('shared.fields.relatedParts'),
    procedures: t('shared.fields.relatedProcedures'),
    subscribers: t('shared.fields.subscribers'),
  }

  return (
    <div className="p-4 overflow-y-auto h-100">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="h4 fw-bold mb-1">{t('assetsSettings.title')}</h1>
          <p className="text-secondary mb-0">{t('assetsSettings.description')}</p>
        </div>
        {showClose && (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-outline-secondary border-0"
            onClick={handleClose}
            aria-label={t('shared.actions.close')}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {tabs.length === 0 ? (
        <div className="text-secondary">
          <h2 className="h6 fw-bold mb-1">{tGeneral('notAuthorized.title')}</h2>
          <p className="mb-0">{tGeneral('notAuthorized.message')}</p>
        </div>
      ) : (
        <>
          <Tabs
            tabs={tabs}
            activeKey={effectiveTab}
            onChange={(key) => setActiveTab(key as AssetsSettingsTab)}
            variant="underline"
            size="sm"
            className="mb-4"
          />

          {/* Form settings tab */}
          {effectiveTab === 'form' && (
            <FormSettingsEditor
              entityType="asset"
              fieldLabels={fieldLabels}
              orderedFields={[...ASSET_FIELD_ORDER]}
              alwaysVisibleFields={ASSET_ALWAYS_VISIBLE}
              alwaysRequiredFields={ASSET_ALWAYS_REQUIRED}
              canEdit={canEditForm}
            />
          )}

          {/* Statuses tab */}
          {effectiveTab === 'statuses' && (
            <div>
              <p className="text-secondary mb-4">{t('assetsSettings.statusesDescription')}</p>
              <AssetStatusesSettings />
            </div>
          )}

          {/* Downtime reasons tab */}
          {effectiveTab === 'downtime-reasons' && (
            <div>
              <AssetDowntimeReasonsSettings />
            </div>
          )}

          {/* Tags tab */}
          {effectiveTab === 'tags' && (
            <div>
              <p className="text-secondary mb-4">{t('assetsSettings.tagsDescription')}</p>
              <AssetTagsSettings />
            </div>
          )}

          {/* Custom fields definition tab */}
          {effectiveTab === 'custom-fields' && (
            <div>
              <p className="text-secondary mb-3">{t('assetsSettings.customFieldsDescription')}</p>
              <CustomFieldsEditor adapter={customFieldsAdapter} canManage={canManage} />
            </div>
          )}

          {effectiveTab === 'qr-code' && <AssetQrLayoutSettings kind="work-request" />}
          {effectiveTab === 'direct-link-qr-code' && <AssetQrLayoutSettings kind="direct-link" />}
        </>
      )}
    </div>
  )
}
